import { useState, useMemo, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import Modal from '../../modal/Modal'
import { TextControl, Button } from '../../generic/Core'
import { Icon_Folder } from '../../../styles/icons'
import * as BsIcons from 'react-icons/bs'

const ROOT = '__root__'
const ALL_DOCS = { id: '__all__', name: 'All Documents', path: null }

// ── Styled components ─────────────────────────────────────────────────────────

const SearchRow = styled.div`
  margin-bottom: 0.75rem;
`

const TreeScroll = styled.div`
  overflow-y: auto;
  max-height: 22rem;
  border: 1px solid ${({ theme }) => theme.border?.default || '#e5e7eb'};
  border-radius: 6px;
  padding: 0.5rem 0.25rem;
`

const FolderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.5rem;
  padding-left: ${({ $depth }) => `${($depth || 0) * 1.25 + 0.5}rem`};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme, $selected }) =>
    $selected ? (theme.color?.primary || '#2563eb') : (theme.font?.label || '#374151')};
  background: ${({ theme, $selected }) =>
    $selected ? (theme.dropdown?.selectedBackground || '#eff6ff') : 'transparent'};
  font-weight: ${({ $selected }) => $selected ? 600 : 400};

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected
        ? (theme.dropdown?.selectedBackground || '#eff6ff')
        : (theme.dropdown?.hoverBackground || theme.area?.highBg || '#f3f4f6')};
  }

  svg {
    font-size: 1rem;
    flex-shrink: 0;
    opacity: 0.65;
  }
`

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 0.875rem;
  transition: transform 0.15s ease;
  transform: ${({ $expanded }) => $expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  font-size: 0.65rem;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
`

const ChevronGap = styled.span`
  display: inline-block;
  width: 0.875rem;
  flex-shrink: 0;
`

const FolderPath = styled.span`
  margin-left: 0.25rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EmptyMsg = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  font-size: 0.875rem;
  padding: 2rem 1rem;
  margin: 0;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`

const SelectedBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
  border-radius: 6px;
  background: ${({ theme }) => theme.dropdown?.selectedBackground || '#eff6ff'};
  color: ${({ theme }) => theme.color?.primary || '#2563eb'};
  font-size: 0.875rem;
  font-weight: 500;
  min-height: 2.25rem;

  svg {
    flex-shrink: 0;
    opacity: 0.75;
  }
`

// ── Component ─────────────────────────────────────────────────────────────────

const MoveFolderModal = ({ item, onClose, onConfirm, isLoading, navigateMode = false, title }) => {
  const { getMe } = useApi()
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [expanded, setExpanded] = useState(new Set())
  const searchRef = useRef(null)

  const { data } = useQuery({
    queryKey: ['folder', 'tree'],
    queryFn: () => getMe('folder?noPagination=true&orderBy=path'),
    staleTime: 30000,
  })

  const folders = data?.data ?? []

  // Build parent → children map
  const childrenOf = useMemo(() => {
    const map = {}
    folders.forEach(f => {
      const pid = f.parentFolderId || ROOT
      if (!map[pid]) map[pid] = []
      map[pid].push(f)
    })
    return map
  }, [folders])

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  // Flat filtered list used when there is a search term
  const searchResults = useMemo(() => {
    if (!search.trim()) return null
    const term = search.toLowerCase()
    return folders.filter(f =>
      f.name?.toLowerCase().includes(term) ||
      f.path?.toLowerCase().includes(term)
    )
  }, [folders, search])

  const handleConfirm = () => {
    if (!selected) return
    onConfirm(selected.id === '__all__' ? null : selected)
  }

  // ── Recursive tree renderer ──────────────────────────────────────────────
  const renderFolder = (folder, depth) => {
    const { id, name } = folder
    const children = childrenOf[id] || []
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(id)
    const isSelected = selected?.id === id

    return (
      <div key={id}>
        <FolderRow
          $depth={depth}
          $selected={isSelected}
          onClick={() => {
            setSelected(folder)
            if (hasChildren) toggle(id)
          }}
        >
          {hasChildren
            ? <Chevron $expanded={isExpanded}><BsIcons.BsChevronRight /></Chevron>
            : <ChevronGap />
          }
          {Icon_Folder}
          {name}
        </FolderRow>
        {isExpanded && children.map(child => renderFolder(child, depth + 1))}
      </div>
    )
  }

  const roots = childrenOf[ROOT] || []

  // ── Render ───────────────────────────────────────────────────────────────
  return ReactDOM.createPortal(
    <Modal setShow={onClose} show={true} mode="fullscreen">
      <Modal.Header onClose={onClose}>
        <h3 style={{ margin: 0 }}>{title || t('documents.document.move.modal.title')}</h3>
      </Modal.Header>
      <Modal.Body>

        {/* Selected folder indicator */}
        <SelectedBar>
          {selected
            ? <>{Icon_Folder}{selected.id === '__all__' ? t('document.folder.all') : (selected.path || selected.name)}</>
            : <span style={{ opacity: 0.5 }}>{t('documents.document.move.modal.noSelection')}</span>
          }
        </SelectedBar>

        {/* Search */}
        <SearchRow>
          <TextControl
            ref={searchRef}
            type="text"
            placeholder={t('documents.document.move.modal.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchRow>

        {/* Tree or flat search results */}
        <TreeScroll>
          {searchResults !== null ? (
            // Flat search results
            searchResults.length === 0
              ? <EmptyMsg>{t('documents.document.move.modal.empty')}</EmptyMsg>
              : searchResults.map(folder => (
                <FolderRow
                  key={folder.id}
                  $depth={0}
                  $selected={selected?.id === folder.id}
                  onClick={() => setSelected(folder)}
                >
                  <ChevronGap />
                  {Icon_Folder}
                  {folder.name}
                  {folder.path && folder.path !== folder.name && (
                    <FolderPath>{folder.path}</FolderPath>
                  )}
                </FolderRow>
              ))
          ) : (
            // Interactive tree
            <>
              {navigateMode && (
                <FolderRow $depth={0} $selected={selected?.id === '__all__'} onClick={() => setSelected(ALL_DOCS)}
                  style={{ fontWeight: 500, borderBottom: '1px solid', borderColor: 'inherit', marginBottom: '0.25rem', paddingBottom: '0.5rem', borderRadius: 0 }}
                >
                  <ChevronGap />
                  {t('document.folder.all')}
                </FolderRow>
              )}
              {roots.length === 0
                ? <EmptyMsg>{t('documents.document.move.modal.empty')}</EmptyMsg>
                : roots.map(folder => renderFolder(folder, 0))
              }
            </>
          )}
        </TreeScroll>

        <Footer>
          <Button $variant="secondary" onClick={onClose} disabled={isLoading}>
            {t('all.modals.footer.cancel')}
          </Button>
          <Button
            $variant="primary"
            onClick={handleConfirm}
            disabled={!selected || isLoading}
          >
            {isLoading ? '...' : t(navigateMode ? 'documents.document.move.modal.select' : 'documents.document.move.modal.confirm')}
          </Button>
        </Footer>

      </Modal.Body>
    </Modal>,
    document.body
  )
}

export default MoveFolderModal
